import { useContext, useEffect, useMemo, useState } from 'react';
import { Handle, NodeToolbar, Position, useConnection, useReactFlow } from '@xyflow/react';
import get from 'lodash/get';
import { Trans } from '@lingui/react/macro';
import { HexColorPicker } from 'react-colorful';

import WorkflowContext from '../../Workflow/WorkflowContext';
import WorkflowStepHandles from './handles/WorkflowStepHandles';
import getStepType from '../../Workflow/stepTypes';
import { useDarkMode } from '../../../../context/DarkModeContext';

function DecisionNode({ id, positionAbsoluteX: x, positionAbsoluteY: y, width, height, isConnectable }) {
  const { state, dispatchAction } = useContext(WorkflowContext);
  const reactFlowInstance = useReactFlow();
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const { isDarkMode } = useDarkMode();

  const step = useMemo(() => {
    return get(state, ['steps', id]);
  }, [state, id]);

  const role = useMemo(() => {
    return get(step, 'role');
  }, [step]);

  const selected = useMemo(() => {
    return state.selection === id;
  }, [state, id]);

  const stepColor = useMemo(() => {
    return step?.color || '#000000';
  }, [step]);

  const borderColor = useMemo(() => {
    if (!role) return 'lightgray';
    if (selected) return 'rgb(19, 117, 237)';

    return stepColor;
  }, [selected, role]);

  const containerStyle = useMemo(
    () => ({
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      width: width,
      height: height,
    }),
    [selected]
  );

  const handleContainerStyle = useMemo(
    () => ({
      position: 'absolute',
      top: 0,
      left: 0,
      width: width,
      height: height,
    }),
    [selected]
  );

  const shapeStyle = useMemo(
    () => ({
      width: width / Math.SQRT2 + 6,
      height: height / Math.SQRT2 + 6,
      borderStyle: 'solid', // isTarget ? 'dashed' : 'solid',
      backgroundColor: isDarkMode ? '#2a2d33' : 'white',
      borderWidth: '1px',
      cursor: 'pointer',
      borderRadius: '10px',
      borderColor: borderColor,
      boxShadow: selected
        ? 'rgba(19, 117, 237, 0.16) 0px 1px 2px, rgb(19, 117, 237) 0px 0px 0px 2px, rgba(19, 117, 237, 0.08) 0px 3px 6px, rgba(19, 117, 237, 0.5) 0px 0px 0px 6px'
        : 'none',
      transform: 'translate(18px, 0px) rotate(45deg)',
    }),
    [selected, isDarkMode]
  );

  const handleSpacing = useMemo(() => {
    const halfWidth = width / 2;

    return Math.floor(Math.sqrt(2 * halfWidth ** 2) - halfWidth) - 2;
  }, [width]);

  useEffect(() => {
    if (!selected) {
      setColorPickerOpen(false);
    }
  }, [selected]);

  return (
    <div className="decision-node-container">
      <NodeToolbar isVisible={selected} position={Position.Top}>
        {colorPickerOpen && (
          <HexColorPicker
            className="nodrag nopan"
            color={stepColor}
            onChange={newColor => {
              dispatchAction.setStepColor({ name: id, color: newColor });
            }}
          />
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '0.7rem' }}>
          <button className="xy-theme__button" onClick={() => dispatchAction.duplicateStep({ name: id, reactFlowInstance })}>
            <Trans>duplicate</Trans>
          </button>
          <button className="xy-theme__button" onClick={() => dispatchAction.removeStep({ name: id, reactFlowInstance })}>
            <Trans>delete</Trans>
          </button>
          <button
            className="xy-theme__button"
            style={{ backgroundColor: stepColor, width: '1rem', height: '1rem', borderRadius: '100%' }}
            onClick={() => setColorPickerOpen(!colorPickerOpen)}
          />
        </div>
      </NodeToolbar>
      <div style={containerStyle}>
        <div style={shapeStyle} />
        <div className="desicion-node" style={handleContainerStyle}>
          <WorkflowStepHandles id={id} width={width} height={height} isConnectable={isConnectable} />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              textWrap: 'balance',
              width,
              height,
              color: role ? (isDarkMode ? '#e2e4e9' : 'black') : borderColor,
            }}
          >
            {step?.title}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DecisionNode;
